import React, { FunctionComponent } from "react";
import classNames from "classnames";
import { Form, DatePicker } from "antd";
import type { Rule } from "antd/lib/form";
import type { NamePath } from "antd/lib/form/interface";
import type { Moment } from "moment";
import FormLabel from "./FormLabel";
import styles from "../BiformLayout.module.scss";
import { QuestionComponentProps } from "../helper";
import { QUESTION_TYPES } from "../constants";

/**
 * Optional extras for a layout that relates two dates (the off-campus request's start and end).
 * The generic renderer passes none of them, so every other form is unaffected.
 */
interface DatePickerProps extends QuestionComponentProps {
  /** Rules checked after the required one, e.g. an end date that must not fall before a start date. */
  extraRules?: Rule[];
  /** Fields whose change re-validates this one, once it has been touched. */
  dependencies?: NamePath[];
  /** Days the picker does not offer. */
  disabledDate?: (current: Moment) => boolean;
}

// DATE_PICKER / DATE_TIME_PICKER — pick a date (and optionally a time).
// The field value is a moment object (antd native); it is serialised to a string in buildResponses.
const DatePickerComponent: FunctionComponent<DatePickerProps> = ({ question, disabled = false, extraRules, dependencies, disabledDate }) => {
  const isDateTime = question.questionType === QUESTION_TYPES.DATE_TIME_PICKER;
  const format = isDateTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";

  return (
    <Form.Item
      name={question.id}
      label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
      rules={[{ required: question.isRequired, message: "Please select a date!" }, ...(extraRules || [])]}
      dependencies={dependencies}
      className={classNames(styles.datePickerInput, styles.disableDatePickerInput)}
    >
      <DatePicker
        disabled={disabled}
        showTime={isDateTime}
        format={format}
        placeholder={question.placeholder || (isDateTime ? "Select date & time" : "Select a date")}
        disabledDate={disabledDate}
        style={{ width: "100%" }}
      />
    </Form.Item>
  );
};

export default DatePickerComponent;
